import { createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// How long the browser waits for one assistant turn / one mic clip (issue #10,
// 20 Oct 2026). There was no limit at all, so a slow AI meant a spinner that
// never stopped. The API answers first (55 s chat, 45 s voice, with a
// sentence); these only catch a connection that never answers at all.
export const ASSISTANT_CHAT_TIMEOUT_MS = 70000;
export const ASSISTANT_VOICE_TIMEOUT_MS = 60000;

// `skipGlobalErrorScreen`: a 503/504 on ONE question (the proxy giving up on a
// slow model) must stay in the chat — the axios interceptor otherwise swaps
// the whole app for its full-page "server error" screen.
const REQUEST_OPTIONS = { skipGlobalErrorScreen: true };

// What a failed call rejects with: the server's body (code + message) plus
// the HTTP status, or TIMEOUT / NETWORK when no answer came back at all.
// The status used to be dropped, so a 429 or a 504 could not be told apart
// from any other failure.
export const assistantFailure = (error, fallback) => {
    const response = error?.response;
    if (response) {
        const body = response.data && typeof response.data === 'object' ? response.data : { message: fallback };
        return { ...body, status: response.status };
    }
    const timedOut = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
    return { message: fallback, code: timedOut ? 'TIMEOUT' : 'NETWORK' };
};

// One assistant turn. Identity is derived from the JWT on the backend — the
// widget only sends the text and a session id. Returns { reply, session_id }.
export const sendAssistantMessage = createAsyncThunk(
    'assistant/sendMessage',
    async ({ message, sessionId, surface = 'widget', replyLang }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            // replyLang is the assistant's own language selection. The backend
            // ignores it for any surface other than the widget, so a choice made
            // in the chat bubble cannot change how AI Reports reads.
            const response = await axiosInstance.post(
                `${baseURL}/assistant/chat`,
                { message, sessionId, surface, replyLang },
                { ...REQUEST_OPTIONS, timeout: ASSISTANT_CHAT_TIMEOUT_MS },
            );
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(assistantFailure(error, 'Assistant unavailable'));
        }
    }
);

// Mic clip → text (backend proxies to the AI's Whisper STT). Returns { text }.
export const sendAssistantVoice = createAsyncThunk(
    'assistant/sendVoice',
    async ({ blob, language }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const form = new FormData();
            form.append('audio', blob, 'voice.webm');
            // Which language the speaker is using. Left out, Whisper guesses and
            // returns Tamil speech written in English.
            if (language && language !== 'auto') form.append('language', language);
            const response = await axiosInstance.post(`${baseURL}/assistant/voice`, form, {
                ...REQUEST_OPTIONS,
                timeout: ASSISTANT_VOICE_TIMEOUT_MS,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(assistantFailure(error, 'Transcription failed'));
        }
    }
);
